/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { BCode } from '@src/app/model/bcode';
import { Cycle } from '@src/app/model/cycle';
import { Schedule } from '@src/app/model/schedule';
import { Txn } from '@src/app/model/txn';
import { Field } from '@src/app/shared/field/Field';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { GridField } from '@src/app/shared/grid/grid-field';
import { maxChars, removeError, required, validateTxnDate } from '@src/app/shared/validators';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { JournalService } from '@src/app/modules/txn/journal.service';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { ActivatedRoute } from '@angular/router';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { first } from 'rxjs/operators';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormGroup } from '@angular/forms';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { BankAccount } from '@src/app/model/bankAccount';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogOptions } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { JournalPageNavRoute } from '@src/app/routes/app-router';

@Component({
    selector: 'app-journal-page',
    templateUrl: './journal-page.component.html',
    styleUrls: ['./journal-page.component.scss'],
    imports: [FormPageComponent]
})
export class JournalPageComponent extends AbstractPageComponent  {
    dataSvc = inject(JournalService);
    bCodeSvc = inject(BCodeService);
    protected activeRoute = inject(ActivatedRoute);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    protected scheduleSvc = inject(ScheduleService);
    protected bankAccountSvc = inject(BankAccountService);
    dialogRef = inject<MatDialogRef<JournalPageComponent>>(MatDialogRef, { optional: true });
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA, { optional: true });


    readonly path = Txn.TYPE.JRNL.code

    bCodes: BCode[] = [];
    bankAccounts: BankAccount[] = [];

    idField = FieldMaker.id();
    refField = FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, {
        cellOpts: { heading: 'Ref' }, readonly: true
    });

    revField = FieldMaker.rev().override({ visible: Field.showAll, readonly: true, });

    cycleField: PicklistField = FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle',
        { service: this.cycleSvc, refreshes: ['periodId'] }, { formColumn: 2, readonly: true, sendServer: false }
    );

    periodField: PicklistField = FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
        { service: this.periodSvc },
        {
            formColumn: 2, readonly: true, sendServer: false,
            refresh: (o: Cycle, control: AppPicklistControl) => {
                if (o) {
                    control.field.setPicklistItems(o.periods);
                    control.setValue(null);
                }
            },
        }
    );

    txnDateField: Field = FormDateComponent.make('Transaction Date', 'txnDate', {
        cellOpts: { width: '2%' }, formColumn: 2,
        validators: [validateTxnDate(this.cycleField, this.periodField)],
    });

    childTxn = this.getChildGrid();

    referenceField = FormTextComponent.make('Name', 'reference').override( {
        validators: [maxChars(30), required], formColumn: 3, hint: 'Enter a short name for this transaction up to 30 characters'
    })

    notesField = FieldMaker.notes().override({
        validators: [required], formColumn: 3, hint: 'Enter a detailed description to explain why this transaction was necessary'
    }).override({visible: Field.showAll});

    schedules: Schedule[];

    debitField = FormNumberComponent.makeCurrency("Total Debits", "totalDebit",
        { formRow: 1, formColumn: 4, readonly: true, visible: Field.formOnly }
    );

    creditField = FormNumberComponent.makeCurrency("Total Credits", "totalCredit",
        { formRow: 1, formColumn: 4, readonly: true, visible: Field.formOnly }
    );

    config = new FormConfig({
        navRoute: JournalPageNavRoute,
        docPath: 'omc-books/general-accounting/journals',
        title: Txn.TYPE.JRNL.name,
        help: $localize`Journal Entries`,
        fieldSet: new FieldSet({
            fields: [

                this.idField,
                this.refField,
                this.revField,

                //FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
                //FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),

                /* txtDateField Validator overwrites period info, need this order! */
                this.txnDateField,
                this.cycleField,
                this.periodField,

                this.referenceField,
                this.notesField,

                this.debitField,
                this.creditField,

                this.childTxn,
            ],
            formLayout: [
                { cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] },
                { cells: [{ colspan: 4, pageTab: 'yes' }] }
            ],
            formValidator: [ (o: FormGroup) => {
                if (this.debitField.control?.value === this.creditField.control?.value) {
                    removeError(o, 'unmatched');
                    return null;
                }
                const diff = this.debitField.control?.value - this.creditField.control?.value;
                return { unmatched: 'Debits do not match credits - difference of ' + diff };

            }]
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newFactory.bind(this),
        configReady: this.configReady,
        beforeEdit: this.enhanceObject.bind(this),
    });

    constructor() {
        const currentUserSvc = inject(CurrentUserService);

        super();

        this.subscriptions.push(forkJoin([currentUserSvc.getCurrentUser().pipe(first()),
        this.scheduleSvc.get(true).pipe(first()),
        this.cycleSvc.get<Cycle>(true),
        this.bCodeSvc.get(true),
        this.bankAccountSvc.get(true),
        ]).subscribe(
            ([currentUser, schedules, cycles, bCodes, bankAccounts]) => {
                this.currentUser = currentUser;
                this.schedules = schedules as Schedule[];
                this.bCodes = bCodes as BCode[];
                this.currentCycle = this.cycleSvc.getCurrentCycle(cycles);
                this.cycleField.setPicklistItems(cycles);
                this.bankAccounts = bankAccounts as BankAccount[];
                this.configReady.next(null);
            }));
    }


    enhanceObject(o: Txn) {
        this.revField.visible = Field.showAll;
        this.refField.visible = Field.showAll;

        o.totalCredit = 0;
        o.totalDebit = 0;
        o.details.forEach ( d => {
            o.totalCredit += d.credit;
            o.totalDebit += d.debit;
        })
        this.config.readonly = this.isYearEndJournal(o);
        return o;
    }

    /*
    * Year end journals carry the closing balances forward, the server refuses to change them.
    * Shown read only so the user is not offered a save that can only fail.
    */
    isYearEndJournal(o: Txn) {
        return o.details.some(
            d => d.typeId === Txn.TYPE.YEAREND_DETAIL.id || d.typeId === Txn.TYPE.SURPLUS.id
        );
    }

    newFactory(): Observable<Txn> {
        this.revField.visible = Field.noShow;
        this.refField.visible = Field.noShow;
        this.config.readonly = false; // May have been set by a year end journal we looked at earlier

        if (this.dialogRef && this.dialogOptions['journalToPost']) {
            const jrnl = this.dialogOptions['journalToPost']
            console.log('Creating journal with defaults ', jrnl);
            return of(jrnl);
        }
        const txn = new Txn();

        txn.ledgerId = Txn.LEDGER.GL.id;
        txn.typeId = Txn.TYPE.JRNL.id;


        txn.totalDebit = 0;
        txn.totalCredit = 0;

        txn.details.push(this.createChildItem(txn))
        return of(txn);
    }

    createChildItem(parent: Txn = null): Txn {
        const txn = new Txn();
        txn.ledgerId = Txn.LEDGER.GL.id;
        txn.typeId = Txn.TYPE.JRNL_DETAIL.id;
        txn.txnDate = parent ? parent.txnDate : this.txnDateField.control.value;
        txn.txnPeriodId = parent ? parent.txnPeriodId : this.periodField.control.value;
        txn.txnCycleId = parent ? parent.txnCycleId : this.cycleField.control.value;
        return txn;
    }

    newChildTxn(): Observable<Txn> {
        return of(this.createChildItem());
    }

    editDebit(newValue: number, f: Field) {
        this.toggleDrCr(newValue, f, 'credit');
    }

    editCredit(newValue: number, f: Field) {
        this.toggleDrCr(newValue, f, 'debit');
    }

    toggleDrCr(newValue: number, f: Field, altSide) {
        const alt = (f.control as AppFormControl).getRow().get(altSide);
        alt.setValue(0, {emitEvent: false});
        if (Math.abs(newValue) > 0) {
            alt.disable({emitEvent: false });
        } else {
            alt.enable({emitEvent: false });
        }
        this.txnValueChanges();
    }

    txnValueChanges(): void {
        const txns = (this.childTxn.control as GridControl).gridRows();
        let totalDebits = 0;
        let totalCredits = 0;
        if (txns) {
            txns.forEach( tRow => {
                if (!tRow.deleted) {
                    totalDebits += tRow.get('debit').value;
                    totalCredits += tRow.get('credit').value;
                }
            });
        }
        // Somehow, without rounding we can get .0000000xx differences in the values...
        this.debitField.control.setValue(Math.round(totalDebits * 100) / 100, { emitEvent: false });
        this.creditField.control.setValue(Math.round(totalCredits * 100) / 100, { emitEvent: false });
    }

    childRow(o: Txn) {
        const bankAccountField = FormPicklistComponent.make('Bank Account', 'bankAccountId', null,
            { items: [], allowSelectNone: true },
            { cellOpts: { minWidth: '15em' } });

        if (o) {
            const bankAccounts = this.bankAccounts.filter(ba => ba.bCodeId === o.bCodeId);
            bankAccountField.setPicklistItems(bankAccounts);
        }

        return [
            FieldMaker.id(),
            FormTextComponent.make('Reference', 'refNr', {
                cellOpts: { heading: 'Ref' }, readonly: true
            }),
            FormComboBoxComponent.make('Account', 'bCodeId', 'bCode',
                {
                    items: this.bCodes,
                    refreshes: [ (bc) => {
                        const filterList = this.bankAccounts.filter(ba => ba.bCodeId === bc.id);
                        bankAccountField.setPicklistItems(filterList);
                        if (filterList.length > 0) {
                            bankAccountField.control.setValue(filterList[0].id, {emitEvent: false});
                        } else {
                            bankAccountField.control.setValue(null, { emitEvent: false });
                        }
                    }]
                },
                { cellOpts: { minWidth: '15em' }, validators: [required] }),

            FormPicklistComponent.make('Schedule', 'scheduleId', 'schedule',
                { service: this.scheduleSvc },
                { cellOpts: { minWidth: '15em' } }),

            bankAccountField,

            FormNumberComponent.makeCurrency("Debit", "debit",
                { valueChanges: this.editDebit.bind(this), cellOpts: { width: '7em' } }
            ),

            FormNumberComponent.makeCurrency("Credit", "credit",
                { valueChanges: this.editCredit.bind(this), cellOpts: { width: '7em' } }
            ),

            FieldMaker.notes().override({ cellOpts: { width: '30%' } }),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow({},{ valueChanges: this.txnValueChanges.bind(this) }),
        ]
    }

    getChildGrid() {
        return new GridField({
            field:
                { label: $localize`Transaction Details`, value: 'details', visible: Field.formOnly, formRow: 2 },
            rowFactory: this.childRow.bind(this),
            objFactory: this.newChildTxn.bind(this),
        });
    }
}
